/**
 * One external DeCA — deliberately light.
 *
 * There is very little to show, and that is the point: this is somebody else's document. There
 * is no estado, no CSV, no QR of ours, no version chain and no Historial, because none of those
 * exist for a file another party generated in another system. What this screen is *for* is the
 * archived copy — attaching it, replacing it, downloading it — since BOE Third.5 lets the issuing
 * party disable its URL seven days after the service while BOE Second.4's retention duty runs for
 * a year, and the copy is the only thing that survives that gap.
 *
 * Four early returns, in the order every detail screen in this portal uses: loading, then the
 * resource that is **not there** (an empty state and a way back, never an alert with a *Reintentar*
 * that cannot succeed), then a genuine failure, then the body.
 */

import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { unwrap } from '@deca/api'

import { api, downloadFile, MIME } from '../app/api.ts'
import { apiErrorMessage, isResourceNotFound } from '../app/errors.ts'
import { attachExternalDecaPdf, kilobytes } from '../app/externalDecas.ts'
import { qk, useExternalDeca } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime, formatNumber } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import {
  Button,
  Card,
  Datum,
  EmptyState,
  ErrorState,
  LoadingRows,
  NoAccessState,
  Notice,
} from '../ui/primitives.tsx'
import { ArchivedCopyTag } from '../ui/EstadoChip.tsx'
import { Icon } from '../ui/Icon.tsx'
import { PdfDropZone, useDroppedFilesStayOnPage } from '../ui/PdfDropZone.tsx'
import { useToast } from '../ui/Toast.tsx'

export function ExternalDecaDetailScreen() {
  const { t, i18n } = useTranslation()
  const { externalDecaId } = useParams({ strict: false }) as { externalDecaId: string }
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const query = useExternalDeca(externalDecaId)
  const reference = query.data

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Above the early returns, and for a viewer too: a PDF dropped here must not take the portal away.
  useDroppedFilesStayOnPage()

  if (!abilities.readsDecas) {
    return (
      <AppShell title={t('externalDecas.detailTitle')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  async function attach(file: File) {
    setBusy(true)
    setError(null)
    try {
      await attachExternalDecaPdf(externalDecaId, file)
      void client.invalidateQueries({ queryKey: qk.externalDeca(externalDecaId) })
      void client.invalidateQueries({ queryKey: qk.externalDecas() })
      toast.success(t('externalDecas.attached'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!window.confirm(t('externalDecas.deleteConfirm'))) return
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.DELETE('/api/v1/external-decas/{externalDecaId}', {
          params: { path: { externalDecaId } },
        }),
      )
      void client.invalidateQueries({ queryKey: qk.externalDecas() })
      toast.success(t('externalDecas.deleted'))
      void navigate({ to: '/decas-externas', search: {} })
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('externalDecas.detailTitle')}>
        <Card>
          <LoadingRows rows={4} columns={2} />
        </Card>
      </AppShell>
    )
  }

  // Not a load failure: a record that has been deleted is gone, and a *Reintentar* against it can
  // only fail again. `isResourceNotFound` keeps this off the API's bare `NOT_FOUND`, which means
  // "no such route" and is worth retrying.
  if (isResourceNotFound(query.error)) {
    return (
      <AppShell title={t('externalDecas.notFoundTitle')}>
        <Card>
          <EmptyState
            icon="search"
            title={t('externalDecas.notFoundTitle')}
            body={t('externalDecas.notFoundBody')}
            action={
              <Button
                variant="primary"
                onClick={() => void navigate({ to: '/decas-externas', search: {} })}
              >
                {t('externalDecas.backToList')}
              </Button>
            }
          />
        </Card>
      </AppShell>
    )
  }

  if (query.error || !reference) {
    return (
      <AppShell title={t('externalDecas.detailTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  const archived = reference.archivedAt !== null
  const downloadCopy = () => {
    void downloadFile(
      `/api/v1/external-decas/${encodeURIComponent(reference.id)}/pdf`,
      MIME.pdf,
      `${reference.id}.pdf`,
    ).catch((caught: unknown) => toast.error(apiErrorMessage(caught, t)))
  }

  return (
    <AppShell
      title={reference.label ?? t('externalDecas.noLabel')}
      subtitle={t('externalDecas.detailTitle')}
      actions={
        <Button icon="chevronLeft" onClick={() => void navigate({ to: '/decas-externas', search: {} })}>
          {t('externalDecas.backToList')}
        </Button>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="detail-actions">
        <ArchivedCopyTag archived={archived} />
        <span className="small muted">
          {formatDateTime(reference.importedAt, i18n.language)} · {reference.importedBy.display}
        </span>
        <span className="spacer" />
        {abilities.writeDecas ? (
          <Button variant="danger" icon="trash" disabled={busy} onClick={() => void remove()}>
            {t('externalDecas.delete')}
          </Button>
        ) : null}
      </div>

      <Card title={t('externalDecas.detailTitle')} subtitle={t('externalDecas.notOurs')}>
        <div className="form-grid form-grid--full">
          <Datum
            label={t('externalDecas.url')}
            value={
              reference.url === null ? (
                // Made from its PDF alone (#184): the copy below is the record.
                <span className="small muted">{t('externalDecas.noLink')}</span>
              ) : (
                // The issuing party's own address, unwrapped. See the note in `ExternalDecasScreen`.
                <a
                  className="small row mono"
                  href={reference.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={t('externalDecas.openExternal')}
                  style={{ gap: 'var(--space-xs)', wordBreak: 'break-all' }}
                >
                  {reference.url}
                  <Icon name="externalLink" size={14} />
                </a>
              )
            }
          />
          <Datum
            label={t('externalDecas.columns.importedAt')}
            value={formatDateTime(reference.importedAt, i18n.language)}
            tabular
          />
          <Datum
            label={t('externalDecas.columns.importedBy')}
            value={reference.importedBy.display}
          />
        </div>
      </Card>

      <Card title={t('externalDecas.copyTitle')}>
        <div className="stack">
          {archived ? (
            <div className="stack">
              <div className="form-grid form-grid--full">
                <Datum
                  label={t('externalDecas.size')}
                  value={
                    reference.archivedBytes != null
                      ? t('externalDecas.sizeValue', {
                          size: formatNumber(kilobytes(reference.archivedBytes), i18n.language),
                        })
                      : null
                  }
                  // When the copy arrived, under the size it weighs. The date is what a later
                  // inspection is actually about — the URL may be long dead by then.
                  hint={t('externalDecas.archivedOn', {
                    date: formatDateTime(reference.archivedAt, i18n.language),
                  })}
                  tabular
                />
                <Datum
                  label={t('externalDecas.checksum')}
                  value={<span className="mono small">{reference.archivedSha256}</span>}
                />
              </div>
              <div className="row">
                <Button variant="primary" icon="download" onClick={downloadCopy}>
                  {t('externalDecas.download')}
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState
              icon="alert"
              title={t('externalDecas.noCopyTitle')}
              body={t('externalDecas.noCopyBody')}
            />
          )}

          {/* Chosen or dropped (#225), by the same checks and into the same request. */}
          {abilities.writeDecas ? (
            <PdfDropZone
              label={archived ? t('externalDecas.replace') : t('externalDecas.attach')}
              busy={busy}
              loading={busy}
              onFile={(file) => void attach(file)}
            />
          ) : null}
        </div>
      </Card>
    </AppShell>
  )
}
